import { NormalButton, CustomButton, CustomTooltip } from '@/shared/ui/components';
import { filterHourlySummaryRanges } from '@/entities/study';
import { inforOrangeIcon } from '@/shared/ui/assets';
import { withModalLayout, adjustStudyTime } from '@/shared/lib';
import classNames from 'classnames';
import _ from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { t } from 'ttag';
import { INSTANT_REPORT_STATUS } from '../../../config/instantReportTab';
import { useCreateInstantReportModal } from './useCreateInstantReportModal';
import type { CreateInstantReportModalProps } from './createInstantReportModal.types';
import './createInstantReportModal.scss';

const CreateInstantReportModal = withModalLayout((({
  handleAfterCreatingInstantReport,
  start,
  stop,
  timezone,
  studyFid,
  studyTimeOffset,
  adjustedStudyDayOffset,
  studyId,
  handleViewExistedInstantReport,
}: CreateInstantReportModalProps) => {
  const {
    state,
    handleDateClick,
    onClickCreate,
    handleViewReportClick,
    getStartDateLocalTime,
    getEndDateLocalTime,
  } = useCreateInstantReportModal({
    handleAfterCreatingInstantReport,
    start,
    stop,
    timezone,
    studyFid,
    studyTimeOffset,
    adjustedStudyDayOffset,
    studyId,
    handleViewExistedInstantReport,
  });

  const weekDayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  /**
   * Renders the study day grid of the instant report date picker. Pads the weekdays preceding the study start, adds one cell per day up to the study end, marks days that are selected or inside the selected range, and disables days whose hourly summary ranges are empty behind an unavailable-ECG-data tooltip.
   *
   * @returns Array of day cell elements, including the leading padding cells.
   */
  const datePickerContentRenderer = () => {
    const startDateLocalTime = getStartDateLocalTime();
    const endDateLocalTime = getEndDateLocalTime();

    const days = [];
    if (startDateLocalTime.format('ddd') !== 'Mon') {
      for (let i = 0; i < weekDayNames.indexOf(startDateLocalTime.format('ddd')); i += 1) {
        days.push({ padding: weekDayNames[i] });
      }
    }

    const diffDaysLocalTime = endDateLocalTime.diff(startDateLocalTime, 'days', true);
    const studyDateRange = Math.ceil(diffDaysLocalTime);
    _.range(0, studyDateRange).forEach((index) => {
      const date = startDateLocalTime.clone().add(index, 'day');
      days.push({
        date,
        disabled: false,
      });
    });

    return days.map((day) => {
      const { date, padding } = day as any;
      if (!date) {
        return <div key={padding} className="content-item empty-date" />;
      }

      const startDate = adjustStudyTime({
        dateMoment: date.clone().startOf('day'),
        studyTimeOffset,
        shouldRevertToServerTime: true,
      });
      const stopDate = adjustStudyTime({
        dateMoment: date.clone().endOf('day'),
        studyTimeOffset,
        shouldRevertToServerTime: true,
      });
      const ranges = filterHourlySummaryRanges({
        summaries: state.ecgDataMap,
        start: startDate,
        stop: stopDate,
        channels: (state.ecgDataMap as any)?.channels,
        samplingFrequency: (state.ecgDataMap as any)?.samplingFrequency,
        willAddDataRanges: true,
      });

      const isSelected =
        date &&
        (state.startDate || state.endDate) &&
        (date.clone().startOf('day').valueOf() ===
          state.startDate?.clone().startOf('day').valueOf() ||
          date.clone().endOf('day').valueOf() === state.endDate?.clone().endOf('day').valueOf());
      const isInsideSelectedRange =
        date &&
        state.startDate &&
        state.endDate &&
        date.clone().startOf('day').valueOf() > state.startDate.clone().startOf('day').valueOf() &&
        date.clone().endOf('day').valueOf() < state.endDate.clone().endOf('day').valueOf();
      const unavailableEcgData = ranges.length === 0;

      return (
        <CustomTooltip
          key={date.valueOf()}
          title={unavailableEcgData ? 'Unavailable ECG data' : ''}
          placement="bottom"
        >
          <div
            className={classNames('content-item', {
              selected: isSelected,
              'inside-selected-range': isInsideSelectedRange,
              'unavailable-ecg-data': unavailableEcgData,
            })}
            onClick={() => {
              if (!unavailableEcgData) {
                handleDateClick(date);
              }
            }}
          >
            {date?.format('M/D')}
          </div>
        </CustomTooltip>
      );
    });
  };

  if (_.isEmpty(state.ecgDataMap)) {
    return (
      <div className="create-instant-report-modal">
        <div className="skeleton-container">
          <Skeleton height="100%" />
        </div>
      </div>
    );
  }

  return (
    <div className="create-instant-report-modal">
      <div>
        <div className="custom-modal-body-container">
          <div className="select-range-picker-message">
            {t`Select a date range to create an instant report (dates with unavailable data are grayed out).`}
          </div>
          <div className="date-picker-container">
            <div className="date-picker-header">
              {weekDayNames.map((day) => (
                <div className="header-item">{day}</div>
              ))}
            </div>
            <div className="date-picker-content">{datePickerContentRenderer()}</div>
          </div>
        </div>
      </div>
      {!_.isEmpty(state.existedReport) ? (
        <div className="warning-message-container">
          {state.existedReport.status === INSTANT_REPORT_STATUS.GENERATED ? (
            <>
              <div className="left-container">
                <img src={inforOrangeIcon} alt="Info icon" />
                {t`A report already exists for the selected time period.`}
              </div>
              <NormalButton
                id="view-report-btn"
                isText
                isBold
                buttonName={t`View report`}
                onClick={handleViewReportClick}
              />
            </>
          ) : (
            <div className="left-container">
              <img src={inforOrangeIcon} alt="Info icon" />
              {state.existedReport.isArtifactReport
                ? t`A report already exists for the selected time period.`
                : t`A report for the selected time period is currently being generated and will be ready within an hour.`}
            </div>
          )}
        </div>
      ) : (
        <div className="custom-modal-footer-container">
          <CustomButton
            id="create-instant-report-btn"
            loading={state.isCreating}
            onClick={onClickCreate}
            size="small"
            disabled={!(state.startDate && state.endDate)}
          >
            {t`Create Instant Report`}
          </CustomButton>
        </div>
      )}
    </div>
  );
}) as any);

(CreateInstantReportModal as any).defaultProps = {
  title: 'Generate instant report',
  width: '600px',
};

export default CreateInstantReportModal;
